/**
 * ServerInfoTab — Read-only table of server devices and their queue data.
 *
 * Props:
 *   deviceData    — array of server status records from getserverstatus
 *   campusId      — current campus being viewed
 *   corporateId   — corporate_id of this campus (used for server link eligibility)
 *   sessionCorpId — the logged-in user's corporate_id from session (dData.corp)
 *   tzOffset      — campus tz_offset; modify_time is stored in UTC
 */

import { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoToLocal, convertIsoUTCToLocal } from '../../../../../libs.jsx';

/* ── AlarmDataPopover — wraps a button, shows data on hover ── */
const AlarmDataPopover = ({ label, data, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  const handleEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 340;
    const spaceBelow = window.innerHeight - rect.bottom;

    // Center the popover horizontally under the button
    const centerX = rect.left + rect.width / 2;
    let left = centerX - popoverW / 2;
    // Clamp so it stays on screen with 8px margin
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));

    setStyle({
      position: 'fixed',
      left: `${left}px`,
      top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {open && (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>{label}</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45', wordBreak: 'break-all' }}>{data || 'No data available'}</span>
        </span>
      )}
    </span>
  );
};

// ── Column definitions ──────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'description', label: 'Server', align: 'left' },
  { key: 'count_process_queue', label: 'Process Count', align: 'center' },
  { key: 'count_aggregate_repeater_queue', label: 'Aggregate Count', align: 'center' },
  { key: 'count_eman_event_queue', label: 'Eman Count', align: 'center' },
  { key: 'count_maint_queue', label: 'Maint. Count', align: 'center' },
  { key: 'count_other_queue', label: 'Other Count', align: 'center' },
  { key: 'recent_alarm_input_time', label: 'Alarm Time', align: 'center' },
  { key: '_alarm_data', label: 'Alarm Data', align: 'center' },
  { key: 'modify_time', label: 'Last Updated', align: 'center' },
];

const ServerInfoTab = ({ deviceData = [], campusId, corporateId, sessionCorpId, tzOffset, showToast }) => {
  const canLink = useMemo(
    () => corporateId != null && sessionCorpId != null && corporateId.toString() === sessionCorpId.toString(),
    [corporateId, sessionCorpId],
  );

  /** Copy text to clipboard and notify user */
  const handleCopy = (text) => {
    if (text && navigator?.clipboard) {
      navigator.clipboard.writeText(text);
      if (showToast) showToast('success', 'Copied to clipboard.');
    }
  };

  return (
    <section className="pp-section">
      {/* Header */}
      <div className="pp-section__header">
        <h3 className="pp-section__title">
          Server Devices
          <span className="pp-section__count">({deviceData.length})</span>
        </h3>
      </div>

      {/* Table */}
      <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
        <table className="dp-table" style={{ minWidth: '900px' }}>
          <thead className="dp-thead">
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key} className="dp-th" style={{ textAlign: col.align }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deviceData.length === 0 ? (
              <tr className="dp-tr">
                <td className="dp-td dp-td--center" colSpan={COLUMNS.length}>
                  <span className="dp-cell-text dp-cell-text--muted">No server devices found</span>
                </td>
              </tr>
            ) : (
              deviceData.map((device, idx) => (
                <tr key={device.palcare_device_id || idx} className="dp-tr" title={!canLink ? 'Impersonate this campus for server linking.' : undefined}>
                  {/* Server description — link if same corp */}
                  <td className="dp-td">
                    {canLink ? (
                      <Link to={`/profiles/server/${device.palcare_device_id}`} className="dp-link">
                        {device.description}
                      </Link>
                    ) : (
                      <span className="dp-cell-text">{device.description}</span>
                    )}
                  </td>

                  {/* Queue counts */}
                  <td className="dp-td dp-td--center">
                    <span className="dp-cell-text">{device.count_process_queue ?? '\u2014'}</span>
                  </td>
                  <td className="dp-td dp-td--center">
                    <span className="dp-cell-text">{device.count_aggregate_repeater_queue ?? '\u2014'}</span>
                  </td>
                  <td className="dp-td dp-td--center">
                    <span className="dp-cell-text">{device.count_eman_event_queue ?? '\u2014'}</span>
                  </td>
                  <td className="dp-td dp-td--center">
                    <span className="dp-cell-text">{device.count_maint_queue ?? '\u2014'}</span>
                  </td>
                  <td className="dp-td dp-td--center">
                    <span className="dp-cell-text">{device.count_other_queue ?? '\u2014'}</span>
                  </td>

                  {/* Alarm Time */}
                  <td className="dp-td dp-td--center" style={{ whiteSpace: 'nowrap' }}>
                    <span className="dp-cell-text">
                      {device.recent_alarm_input_time
                        ? convertIsoToLocal(device.recent_alarm_input_time)
                        : '\u2014'}
                    </span>
                  </td>

                  {/* Alarm Data — copy-to-clipboard buttons with hover popovers */}
                  <td className="dp-td dp-td--center" style={{ whiteSpace: 'nowrap' }}>
                    <AlarmDataPopover label="Raw Alarm Data" data={device.recent_alarm_input_raw}>
                      <button
                        type="button"
                        className="dp-btn dp-btn--ghost dp-btn--sm"
                        onClick={() => handleCopy(device.recent_alarm_input_raw)}
                        aria-label="Copy raw alarm data"
                        style={{ marginRight: '0.25rem' }}
                      >
                        <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                        </svg>
                      </button>
                    </AlarmDataPopover>
                    <AlarmDataPopover label="Parsed Alarm Data" data={device.recent_alarm_input_parsed}>
                      <button
                        type="button"
                        className="dp-btn dp-btn--ghost dp-btn--sm"
                        onClick={() => handleCopy(device.recent_alarm_input_parsed)}
                        aria-label="Copy parsed alarm data"
                      >
                        <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
                        </svg>
                      </button>
                    </AlarmDataPopover>
                  </td>

                  {/* Last Updated */}
                  <td className="dp-td dp-td--center" style={{ whiteSpace: 'nowrap' }}>
                    <span className="dp-cell-text">
                      {device.modify_time
                        ? convertIsoUTCToLocal(device.modify_time, Number(tzOffset) || 0)
                        : '\u2014'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default ServerInfoTab;
